import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { RuntimeConfig } from '@/lib/runtime-config';
import {
  type AssignmentSet,
  type RepoSummary,
  createAssignmentSet,
  deleteAssignmentSet,
  listAssignmentSets,
  listRepos,
  updateAssignmentSet,
  getBootstrapConfig,
  updateBootstrapConfig,
  type BootstrapConfig
} from '@/lib/web-api';

type ConfigurationPageProps = {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
};

const defaultBootstrapConfig: BootstrapConfig = {
  control_plane: {
    auth_method: 'entra',
    api_url: '',
    entra_api_url: '',
    entra_login_url: '',
    entra_client_id: '',
    realtime_url: '',
    region: 'ap-southeast-2'
  }
};

const formatTimestamp = (value?: number): string => {
  if (!value) {
    return 'Not set';
  }
  return new Date(value).toLocaleString();
};

const normalizeIds = (ids: string[]): string[] => {
  const unique = Array.from(new Set(ids));
  unique.sort();
  return unique;
};

export const ConfigurationPage = ({ runtimeConfig, tenantId }: ConfigurationPageProps) => {
  const [bootstrapText, setBootstrapText] = useState('');
  const [bootstrapUpdatedAt, setBootstrapUpdatedAt] = useState<number | undefined>(undefined);
  const [bootstrapPublicUrl, setBootstrapPublicUrl] = useState<string | undefined>(undefined);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [bootstrapSaving, setBootstrapSaving] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  const [assignmentSets, setAssignmentSets] = useState<AssignmentSet[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(true);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [repoLoading, setRepoLoading] = useState(true);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<AssignmentSet | null>(null);
  const [setName, setSetName] = useState('');
  const [selectedRepoIds, setSelectedRepoIds] = useState<string[]>([]);
  const [setSaving, setSetSaving] = useState(false);
  const [setError, setSetError] = useState<string | null>(null);

  const loadBootstrap = useCallback(async () => {
    setBootstrapLoading(true);
    setBootstrapError(null);
    try {
      const response = await getBootstrapConfig(runtimeConfig);
      const hasConfig = response.config && Object.keys(response.config).length > 0;
      const resolved = hasConfig ? response.config : defaultBootstrapConfig;
      setBootstrapText(JSON.stringify(resolved, null, 2));
      setBootstrapUpdatedAt(response.updated_at_unix_ms);
      setBootstrapPublicUrl(response.public_url);
    } catch (err) {
      setBootstrapError(err instanceof Error ? err.message : 'Unable to load bootstrap configuration.');
    } finally {
      setBootstrapLoading(false);
    }
  }, [runtimeConfig]);

  const loadAssignmentSets = useCallback(async () => {
    setAssignmentLoading(true);
    setAssignmentError(null);
    try {
      const sets = await listAssignmentSets(runtimeConfig, tenantId);
      setAssignmentSets(sets);
    } catch (err) {
      setAssignmentError(err instanceof Error ? err.message : 'Unable to load assignment sets.');
    } finally {
      setAssignmentLoading(false);
    }
  }, [runtimeConfig, tenantId]);

  const loadRepos = useCallback(async () => {
    setRepoLoading(true);
    setRepoError(null);
    try {
      const repoList = await listRepos(runtimeConfig, tenantId);
      setRepos(repoList);
    } catch (err) {
      setRepoError(err instanceof Error ? err.message : 'Unable to load repositories.');
    } finally {
      setRepoLoading(false);
    }
  }, [runtimeConfig, tenantId]);

  useEffect(() => {
    void loadBootstrap();
    void loadAssignmentSets();
    void loadRepos();
  }, [loadBootstrap, loadAssignmentSets, loadRepos]);

  const handleBootstrapSave = useCallback(async () => {
    setBootstrapSaving(true);
    setBootstrapError(null);
    try {
      const parsed = JSON.parse(bootstrapText) as BootstrapConfig;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Config must be a JSON object.');
      }
      await updateBootstrapConfig(runtimeConfig, parsed);
      await loadBootstrap();
    } catch (err) {
      setBootstrapError(err instanceof Error ? err.message : 'Invalid JSON configuration.');
    } finally {
      setBootstrapSaving(false);
    }
  }, [bootstrapText, loadBootstrap, runtimeConfig]);

  const openCreateSet = useCallback(() => {
    setEditingSet(null);
    setSetName('');
    setSelectedRepoIds([]);
    setSetError(null);
    setSetDialogOpen(true);
  }, []);

  const openEditSet = useCallback((set: AssignmentSet) => {
    setEditingSet(set);
    setSetName(set.set_name);
    setSelectedRepoIds(set.repo_ids);
    setSetError(null);
    setSetDialogOpen(true);
  }, []);

  const handleSaveSet = useCallback(async () => {
    setSetSaving(true);
    setSetError(null);
    try {
      const repoIds = normalizeIds(selectedRepoIds);
      if (!setName.trim()) {
        throw new Error('Set name is required.');
      }
      if (repoIds.length === 0) {
        throw new Error('Select at least one repository.');
      }
      if (editingSet) {
        await updateAssignmentSet(runtimeConfig, tenantId, editingSet.set_id, {
          set_name: setName.trim(),
          repo_ids: repoIds
        });
      } else {
        await createAssignmentSet(runtimeConfig, tenantId, {
          set_name: setName.trim(),
          repo_ids: repoIds
        });
      }
      setSetDialogOpen(false);
      await loadAssignmentSets();
    } catch (err) {
      setSetError(err instanceof Error ? err.message : 'Unable to save assignment set.');
    } finally {
      setSetSaving(false);
    }
  }, [editingSet, loadAssignmentSets, runtimeConfig, selectedRepoIds, setName, tenantId]);

  const handleDeleteSet = useCallback(async (set: AssignmentSet) => {
    if (!window.confirm(`Delete assignment set "${set.set_name}"?`)) {
      return;
    }
    try {
      await deleteAssignmentSet(runtimeConfig, tenantId, set.set_id);
      await loadAssignmentSets();
    } catch (err) {
      setAssignmentError(err instanceof Error ? err.message : 'Unable to delete assignment set.');
    }
  }, [loadAssignmentSets, runtimeConfig, tenantId]);

  const publicUrlLabel = useMemo(() => {
    if (bootstrapPublicUrl) {
      return bootstrapPublicUrl;
    }
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/bootstrap.json`;
    }
    return 'Unavailable';
  }, [bootstrapPublicUrl]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">Configuration</h2>
        <p className="text-sm text-muted-foreground">
          Manage assignment sets and control-plane bootstrap settings.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Assignment sets</CardTitle>
          <CardDescription>Group repositories and assign them to devices.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                void loadAssignmentSets();
                void loadRepos();
              }}
              disabled={assignmentLoading || repoLoading}
            >
              {assignmentLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
            <Button onClick={openCreateSet}>
              <Plus className="mr-2 h-4 w-4" />
              New set
            </Button>
          </div>

          {assignmentError ? (
            <Alert className="border-error/30 bg-error/10">
              <AlertTitle>Assignment error</AlertTitle>
              <AlertDescription>{assignmentError}</AlertDescription>
            </Alert>
          ) : null}
          {repoError ? (
            <Alert className="border-error/30 bg-error/10">
              <AlertTitle>Repository error</AlertTitle>
              <AlertDescription>{repoError}</AlertDescription>
            </Alert>
          ) : null}

          {assignmentLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading assignment sets
            </div>
          ) : assignmentSets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No assignment sets configured yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Repos</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignmentSets.map(set => (
                  <TableRow key={set.set_id}>
                    <TableCell className="font-medium">{set.set_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {set.repo_ids.join(', ')}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatTimestamp(set.updated_at_unix_ms)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditSet(set)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => void handleDeleteSet(set)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Separator />

      <Card>
        <CardHeader>
          <CardTitle>Bootstrap JSON</CardTitle>
          <CardDescription>Hosted at {publicUrlLabel}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 rounded-lg border border-border bg-muted/40 p-4 text-sm md:grid-cols-2">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Last updated</p>
              <p className="font-medium">{formatTimestamp(bootstrapUpdatedAt)}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Source</p>
              <p className="font-medium">Website bootstrap.json</p>
            </div>
          </div>

          <textarea
            className="min-h-[320px] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={bootstrapText}
            onChange={event => setBootstrapText(event.target.value)}
            spellCheck={false}
          />

          {bootstrapError ? (
            <Alert className="border-error/30 bg-error/10">
              <AlertTitle>Bootstrap error</AlertTitle>
              <AlertDescription>{bootstrapError}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end">
          <Button onClick={() => void handleBootstrapSave()} disabled={bootstrapSaving || bootstrapLoading}>
            {bootstrapSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save bootstrap
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={setDialogOpen} onOpenChange={setSetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSet ? 'Edit assignment set' : 'New assignment set'}</DialogTitle>
            <DialogDescription>
              Define the repos that will be assigned to devices.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Set name</label>
              <Input
                value={setName}
                onChange={event => setSetName(event.target.value)}
                placeholder="Engineering devices"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Repositories</label>
              {repoLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading repositories
                </div>
              ) : repos.length === 0 ? (
                <p className="text-sm text-muted-foreground">No repositories available yet.</p>
              ) : (
                <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-border bg-background p-3">
                  {repos.map(repo => (
                    <label key={repo.repo_id} className="flex items-start gap-2 rounded-md px-1 py-1 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedRepoIds.includes(repo.repo_id)}
                        onChange={() => {
                          setSelectedRepoIds(prev => (
                            prev.includes(repo.repo_id)
                              ? prev.filter(id => id !== repo.repo_id)
                              : [...prev, repo.repo_id]
                          ));
                        }}
                      />
                      <span>
                        <span className="block font-medium">{repo.repo_name}</span>
                        <span className="block text-xs text-muted-foreground">{repo.repo_id}</span>
                      </span>
                    </label>
                  ))}
                  {selectedRepoIds
                    .filter(id => !repos.some(repo => repo.repo_id === id))
                    .map(repoId => (
                      <label key={repoId} className="flex items-start gap-2 rounded-md px-1 py-1 text-sm text-muted-foreground">
                        <input type="checkbox" className="mt-1" checked readOnly />
                        <span>
                          <span className="block font-medium">Missing repository</span>
                          <span className="block text-xs">{repoId}</span>
                        </span>
                      </label>
                    ))}
                </div>
              )}
            </div>
            {setError ? (
              <p className="text-sm text-destructive">{setError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleSaveSet()} disabled={setSaving || repoLoading}>
              {setSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving
                </>
              ) : (
                'Save set'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
